import { useCallback, useState } from 'react';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { getCookie } from 'cookies-next';
import { useAuthStore } from '@/context/global/stores/authStore';
import { getOrCreateDeviceId } from '@/features/auth/utils/deviceId';
import { TSongDetailRes } from '@/context/global/api/helper/song';
import { TApiSuccessResponse } from '@/context/global/types/api';

export const useFetchSongDetail = (isKaraoke: boolean) => {
  const { token: accessToken, user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  const fetchSongDetail = useCallback(
    async (id: number): Promise<AxiosResponse<TApiSuccessResponse<TSongDetailRes>>> => {
      setIsLoading(true);

      try {
        const lang = (await getCookie('locale')) ?? 'th';
        const isPreview = user?.badge === 0;
        const res = await axios.get<TApiSuccessResponse<TSongDetailRes>>(
          `/api/song/${id}`,
          {
            headers: {
              Authorization: accessToken,
              'X-Device-Id': getOrCreateDeviceId(),
              'X-Language': lang ?? 'th',
            },
            params: {
              mode: isKaraoke ? 'vocal' : 'karaoke',
              ...(isPreview && { preview: 'true' }),
            },
          },
        );

        return res;
      } catch (error) {
        // badge=0 + เพลงฟรี (conditions=0) → API return 412 แต่มี link → treat เป็น success
        if (isAxiosError(error) && error.response?.data?.code === 412) {
          const conditions = error.response?.data?.responseObject?.conditions;
          if (conditions === 0) {
            return error.response as AxiosResponse<TApiSuccessResponse<TSongDetailRes>>;
          }
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, isKaraoke, user?.badge],
  );

  return {
    fetchSongDetail,
    isLoading,
  };
};
